import * as React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';

export type TypeAheadOption = {
  object: any;
  label: string;
  disabled?: boolean;
};

type Props = GridRenderEditCellParams & {
  options: TypeAheadOption[];
  placeholder?: string;
  disabled?: boolean;
};

export function GridAutocompleteEditCell(props: Props) {
  const apiRef = useGridApiContext();
  const { id, field, value, options, disabled, placeholder } = props;

  const [open, setOpen] = React.useState(true);
  const [inputValue, setInputValue] = React.useState('');

  const selectedValue = (value ?? null) as TypeAheadOption | null;

  //open immediately when editor mounts
  React.useEffect(() => {
    setOpen(true);
  }, []);

  const forceOpen = () => setOpen(true);

  return (
    <div style={{ width: '100%', height: '100%' }} onMouseDown={forceOpen} onKeyDown={forceOpen}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disablePortal={false}
        value={selectedValue}
        inputValue={inputValue}
        onInputChange={(_e, v) => setInputValue(v)}
        options={options}
        disabled={disabled}
        autoHighlight
        clearOnBlur={false}
        sx={{ width: '100%' }}
        getOptionLabel={o => typeof o === 'string' ? o : (o?.label ?? '')}
        isOptionEqualToValue={(a, b) => a?.object?.ID === b?.object?.ID}
        getOptionDisabled={o => o.disabled === true}
        componentsProps={{
          paper: {
            sx: {
              minWidth: '250px',
            },
          },
        }}
        onChange={async (_e, newValue, reason) => {
          if (reason === 'clear') {
            //  explicitly set empty object or null
            await apiRef.current.setEditCellValue({
              id,
              field,
              value: {},
            });

            apiRef.current.stopCellEditMode({ id, field });
            return;
          }

          await apiRef.current.setEditCellValue({
            id,
            field,
            value: newValue,
          });

          apiRef.current.stopCellEditMode({ id, field });
        }}
        renderInput={params => (
          <TextField
            {...params}
            autoFocus
            placeholder={placeholder}
            variant="standard"
            fullWidth
            onMouseDown={e => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
            onKeyDown={() => setOpen(true)}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
            }}
            sx={{
              '& .MuiInputBase-root': {
                height: '100%',
                px: 1,
                fontSize: 'inherit',
                alignItems: 'center',
              },
            }}
          />
        )}
      />
    </div>
  );
}
